import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import { Container, Typography, Stack, IconButton, InputAdornment, TextField, Link, CircularProgress } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api';
import Logo from '../logo/Logo';
import Iconify from '../iconify/Iconify';
import useResponsive from '../../hooks/useResponsive';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: theme.customShadows.card,
  backgroundColor: theme.palette.background.default,
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(12, 0),
}));

export default function ForgotPasswordComponent() {
  const mdUp = useResponsive('up', 'md');
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const [otp, setOtp] = useState('');
  const [account, setAccount] = useState({ email: '', password: '', password_confirmation: '', name: '', role: 'admin' });
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [showResend, setShowResend] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAccount({ ...account, [name]: value });
  };

  useEffect(() => {
    if (secondsLeft === 0) {
      setShowResend(true);
      return;
    }

    const timer = setTimeout(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const handleResetPassword = () => {
    if(verifyPassword()){
      setShowLoader(true);
      api.post('/forgot_password', { email: account.email })
        .then((response) => {
          setShowLoader(false);
          if (response.data.message) {
            toast.success("OTP sent on your email! Please verify.");
            setOtpMode(true);
          } else if (response.data.error) {
            toast.error(response.data.error);
          }
        })
        .catch(() => {
          setShowLoader(false);
          toast.error("Something went wrong. Please try again.");
        });
      }
  };

  const handleVerifyOtp = () => {
    setShowLoader(true);
    api.post('/reset_password', { email: account.email, 
        otp,
        new_password: account.password,
        user: {
          email:account.email,
          name: account.name,
          password: account.password,
          password_confirmation: account.password_confirmation,
          role:account.role
        } })
      .then((response) => {
        setShowLoader(false);
        if (response.data.success) {
          toast.success(response.data.message);
          navigate('/login');
        } else {
          toast.error("Invalid OTP. Try again.");
        }
      })
      .catch(() => {
        setShowLoader(false);
        toast.error("OTP verification failed.");
      });
  };

  const verifyPassword = () =>{
    if(account.password == account.password_confirmation){
      return true
    }
    else{
      toast.error("Passwords do not match.");

    }
  }

  const showSendAgainOtp = () => {
    setSecondsLeft(30);
    setShowResend(false);
    // trigger resend OTP API call here if needed
    handleResetPassword()
  };
  return (
    <>
      <Helmet>
        <title>City Courier - Reset Password</title>
      </Helmet>

      <StyledRoot>
        <Logo sx={{ position: 'fixed', top: { xs: 16, sm: 24, md: 40 }, left: { xs: 16, sm: 24, md: 40 } }} />

        {mdUp && (
          <StyledSection>
            <Typography variant="h3" sx={{ px: 5, mt: 10, mb: 5 }}>
              Hi, {otpMode ? "Verify Your OTP" : "Reset Password"}<br/> City Courier Services
            </Typography>
            <img src="/assets/illustrations/illustration_login.png" alt="login" />
          </StyledSection>
        )}

        <Container maxWidth="sm">
          <StyledContent>
            <Typography variant="h4" gutterBottom>
              {otpMode ? "Verify OTP" : "Reset Password"}
            </Typography>

            <Typography variant="body2" sx={{ mb: 5 }}>
              Already have an account? <Link variant="subtitle2"  href="/login">Login</Link>
            </Typography>

            {otpMode ? (
  
              <Stack spacing={3} mb={3}>
                <TextField name="otp" label="Enter OTP" value={otp} onChange={(e) => setOtp(e.target.value)} />
                <Typography variant="body2" sx={{ mb: 5 }}>
                    {showResend ? (
                      <Link variant="subtitle2" onClick={showSendAgainOtp} sx={{ cursor: 'pointer' }}>
                        Resend OTP
                      </Link>
                    ) : (
                      <>Resend OTP in: {secondsLeft}s</>
                    )}
                  </Typography>
                <LoadingButton
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={showLoader}
                >
                 
                  {showLoader ? <CircularProgress size={24} /> : "Verify OTP"}
                </LoadingButton>
              </Stack>
            ) : (
              <Stack spacing={3} mb={3}>
                <TextField name="email" label="Email address" onChange={handleChange} />
                <TextField
                  name="password"
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  name="password_confirmation"
                  label="Confirm New Password"
                  type={showPassword ? 'text' : 'password'}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <LoadingButton
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={handleResetPassword}
                  disabled={showLoader}
                >
                  {showLoader ? <CircularProgress size={24} /> : "Reset"}
                </LoadingButton>
              </Stack>
            )}
          </StyledContent>
        </Container>
      </StyledRoot>
    </>
  );
}
